import React, { useCallback, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Collapse,
  IconButton,
  Input,
  CircularProgress,
  useTheme,
} from "@mui/material";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import SportsFootballOutlinedIcon from "@mui/icons-material/SportsFootballOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { radius, surfaceTokens } from "../config/theme";
import { useStatusColors } from "../helpers/statusColors";
import { useDraftData } from "../context/DraftDataContext";

const TAP_MOVE_THRESHOLD_PX = 10;

const buildFeatureCards = (primary: string, good: string, warn: string) => [
  {
    icon: <BarChartOutlinedIcon sx={{ fontSize: 32, color: primary }} />,
    title: "Exposure Analysis",
    desc: "See exactly how much you own each player across all your drafts.",
  },
  {
    icon: <TrendingUpOutlinedIcon sx={{ fontSize: 32, color: good }} />,
    title: "CLV Tracking",
    desc: "Know if you're drafting players above or below market value.",
  },
  {
    icon: <SportsFootballOutlinedIcon sx={{ fontSize: 32, color: warn }} />,
    title: "Stacking Insights",
    desc: "Discover your most common 2- and 3-player combos across your teams.",
  },
];

const HOW_TO_SECTIONS = [
  {
    title: "Export Your Draft Data from Underdog",
    type: "ol" as const,
    items: [
      "Log in to your Underdog Fantasy account.",
      "Go to your completed NFL Season drafts.",
      'Find the "Email exposure CSV" option and export your draft results as a CSV file.',
      "Save the file to your computer.",
    ],
  },
  {
    title: "Upload Your CSV File",
    type: "ol" as const,
    items: [
      "Open the rosterOS Draft Visualizer website in your browser.",
      'Click the "Upload CSV" button at the top.',
      "Select and upload your Underdog CSV file.",
    ],
  },
  {
    title: "Explore Your Draft Portfolio",
    type: "ul" as const,
    intro:
      "Your data is processed in your browser and never leaves your device. Use the navigation to explore:",
    items: [
      "Dashboard: Charts of your draft habits, exposure, value over the market, and positional tendencies.",
      "My Players: Every player you've drafted, with exposure, average draft position (ADP), and closing line value (CLV). Filter by name, team, position, or ADP.",
      "My Teams: Browse all your teams, with each roster's CLV, byes, and playoff stacks.",
      "Insights: Your stack rates, how you build each position, and gaps like premium players you've faded or pairs you rarely draft together.",
    ],
  },
  {
    title: "Tips & Privacy",
    type: "ul" as const,
    items: [
      "All data stays on your device. Nothing is uploaded or shared.",
      "Use filters and sorting to analyze your portfolio in detail.",
      "For the most up-to-date analysis, export your latest Underdog drafts and re-upload them here.",
    ],
  },
];

export default function HeroEmptyState() {
  const { handleFile, isParsing, error } = useDraftData();
  const { palette } = useTheme();
  const primary = surfaceTokens(palette.mode === "dark").brandLight;
  const sc = useStatusColors();
  const featureCards = buildFeatureCards(primary, sc.good, sc.warn);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const suppressClickRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);

  const handleDropZoneClick = useCallback(() => {
    if (isParsing) return;
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    fileInputRef.current?.click();
  }, [isParsing]);

  const handleDropZoneTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length !== 1) return;
    suppressClickRef.current = false;
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
  }, []);

  const handleDropZoneTouchMove = useCallback((e: React.TouchEvent) => {
    const start = touchStartRef.current;
    if (!start || e.touches.length !== 1) return;
    const deltaX = Math.abs(e.touches[0].clientX - start.x);
    const deltaY = Math.abs(e.touches[0].clientY - start.y);
    if (deltaX > TAP_MOVE_THRESHOLD_PX || deltaY > TAP_MOVE_THRESHOLD_PX) {
      suppressClickRef.current = true;
    }
  }, []);

  const handleDropZoneTouchEnd = useCallback(() => {
    touchStartRef.current = null;
  }, []);

  const handleDropZoneTouchCancel = useCallback(() => {
    touchStartRef.current = null;
    suppressClickRef.current = false;
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (!file) return;
      const syntheticEvent = {
        target: { files: [file] } as unknown as HTMLInputElement,
      } as React.ChangeEvent<HTMLInputElement>;
      handleFile(syntheticEvent);
    },
    [handleFile]
  );

  return (
    <Box
      sx={{
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: 2, md: 6 },
        py: { xs: 4, md: 8 },
        background: "radial-gradient(ellipse at 60% 10%, #e8f5e220 0%, transparent 60%), radial-gradient(ellipse at 20% 80%, #e0ede020 0%, transparent 60%)",
      }}
    >
      {/* Logo + headline */}
      <Box sx={{ textAlign: "center", mb: 5 }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: 28, md: 38 },
            fontWeight: 800,
            color: primary,
            letterSpacing: "-0.5px",
            mb: 1,
          }}
        >
          rosterOS
        </Typography>
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: 20, md: 26 },
            fontWeight: 700,
            color: "text.primary",
            mb: 1.5,
          }}
        >
          Analyze Your Best Ball Portfolio
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ maxWidth: 520, mx: "auto", lineHeight: 1.7 }}
        >
          Upload your Underdog and DraftKings draft exports to see exposure, CLV,
          combo analysis, and ADP trends across all your drafts in one place. Load
          both platforms — each pick is scored against its own market.
        </Typography>
      </Box>

      {/* Drop zone */}
      <Box
        onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={handleDropZoneClick}
        onTouchStart={handleDropZoneTouchStart}
        onTouchMove={handleDropZoneTouchMove}
        onTouchEnd={handleDropZoneTouchEnd}
        onTouchCancel={handleDropZoneTouchCancel}
        data-no-swipe="true"
        sx={{
          width: "100%",
          maxWidth: 560,
          border: isDragging ? `2px solid ${primary}` : "2px dashed #C4C4C4",
          borderRadius: `${radius.lg}px`,
          py: { xs: 4, md: 6 },
          px: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
          cursor: isParsing ? "default" : "pointer",
          bgcolor: isDragging ? `${primary}08` : "background.paper",
          transition: "border-color 0.15s, background-color 0.15s",
          touchAction: "pan-y",
          boxShadow: isDragging ? `0 0 0 4px ${primary}18` : "none",
          "&:hover": {
            borderColor: primary,
            bgcolor: `${primary}06`,
          },
        }}
      >
        <Input
          inputRef={fileInputRef}
          type="file"
          inputProps={{ accept: ".csv" }}
          onChange={handleFile}
          sx={{ display: "none" }}
        />
        {isParsing ? (
          <CircularProgress size={36} sx={{ color: primary }} />
        ) : (
          <FolderOpenOutlinedIcon
            sx={{
              fontSize: 52,
              color: primary,
              transform: isDragging ? "scale(1.15)" : "scale(1)",
              transition: "transform 0.15s",
            }}
          />
        )}
        <Typography variant="h3" sx={{ fontWeight: 600, fontSize: 17, textAlign: "center" }}>
          {isParsing ? "Parsing your drafts…" : "Drop your Underdog or DraftKings CSV here"}
        </Typography>
        {!isParsing && (
          <Typography variant="body2" color="text.secondary" textAlign="center">
            or click to browse &nbsp;·&nbsp; Accepts .csv exported from Underdog or DraftKings
          </Typography>
        )}
        {error && (
          <Typography variant="caption" color="error.main" textAlign="center" sx={{ mt: 0.5 }}>
            {error}
          </Typography>
        )}
      </Box>

      {/* Feature preview cards */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mt: 5,
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        {featureCards.map(card => (
          <Paper
            key={card.title}
            elevation={1}
            sx={{
              width: 160,
              p: 2.5,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              textAlign: "center",
              borderRadius: `${radius.md}px`,
            }}
          >
            {card.icon}
            <Typography variant="subtitle2" fontWeight={700} fontSize={13}>
              {card.title}
            </Typography>
            <Typography variant="caption" color="text.secondary" lineHeight={1.5}>
              {card.desc}
            </Typography>
          </Paper>
        ))}
      </Box>

      {/* FAQ link */}
      <Box sx={{ mt: 4, textAlign: "center" }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            cursor: "pointer",
            color: "text.secondary",
            "&:hover": { color: primary },
            transition: "color 0.15s",
          }}
          onClick={() => setFaqOpen(v => !v)}
          role="button"
          aria-expanded={faqOpen}
        >
          <Typography variant="body2" sx={{ fontStyle: "italic" }}>
            "Where do I find my draft exports?"
          </Typography>
          <IconButton size="small" sx={{ p: 0.25 }}>
            <ExpandMoreIcon
              sx={{
                fontSize: 18,
                transform: faqOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
              }}
            />
          </IconButton>
          <Typography variant="body2" color="primary" fontWeight={600}>
            Show me →
          </Typography>
        </Box>

        <Collapse in={faqOpen}>
          <Box
            sx={{
              mt: 2,
              textAlign: "left",
              maxWidth: 520,
              mx: "auto",
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: `${radius.md}px`,
              p: 3,
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
            }}
          >
            {HOW_TO_SECTIONS.map((section) => (
              <Box key={section.title}>
                <Typography variant="body2" fontWeight={700} mb={0.75}>
                  {section.title}
                </Typography>
                {"intro" in section && section.intro && (
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {section.intro}
                  </Typography>
                )}
                <Box
                  component={section.type}
                  sx={{ pl: 2.5, m: 0, display: "flex", flexDirection: "column", gap: 0.75 }}
                >
                  {section.items.map((item, i) => (
                    <Box component="li" key={i}>
                      <Typography variant="body2" color="text.secondary">
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
}
